import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, ButtonGroup, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import type {
  FulfillmentAdminClient,
  MarketingImportBatch,
  MarketingImportColumn,
  MarketingImportColumnNeed,
  MarketingImportDuplicate,
  MarketingImportOrderResult,
  MarketingImportPreview,
} from '@/infrastructure/FulfillmentAdminClient';
import { duplicateFrom } from '@/marketing/lib/errorMessage';
import { mergeNoticeText } from '@/utils/fulfillmentMarketing';
import {
  MARKETING_IMPORT_ACCEPT,
  alignToTemplate,
  downloadMarketingImportTemplate,
  readAsCsv,
} from '@/utils/marketingImportFile';
import { EMPTY_CELL } from '@/utils/fulfillmentOrderColumns';
import { kstTimestamp } from '@/utils/fulfillmentTime';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { useAuth } from '@/providers/AuthProvider';
import { ROUTES } from '@/routes/routes';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { DownloadIcon, PencilIcon } from '@/pages/fulfillment/components/UltimateIcons';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import '../../ultimateDesign.css';
import './addOrdersModal.css';

const GENERIC_ERROR = 'The marketing operations request failed.';
/** A file can carry hundreds of rows; the desk scans the head of it and reads the rest as reasons below. */
const PREVIEW_ROWS = 50;
/** The preset the desk reaches for almost every time; anything else is typed in [wireframe M1]. */
const SEEDING_ORDER_TYPE = 'Influencer Seeding';
/** The server refuses a longer order type or PIC, so the box stops there instead of the confirm. */
const BATCH_FIELD_MAX_LENGTH = 128;
/** Enough of the import record to find the batch just confirmed and the ones around it. */
const RECENT_IMPORTS = 10;

/** The three answers to "do I have to fill this in", in the desk's words. */
const NEED_LABEL: Record<MarketingImportColumnNeed, string> = {
  required: 'Required',
  conditional: 'Some countries',
  optional: 'Optional',
};

/** Counts sit in running text here, and "1 errors" reads as a bug in the screen rather than in the file. */
const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

interface MarketingImportFormProps {
  readonly client: FulfillmentAdminClient;
  /** True only while a confirm is with the server: that write cannot be called back, so the modal holds. */
  readonly onConfirmingChange: (confirming: boolean) => void;
  readonly onImported: () => void;
}

function MarketingImportForm({ client, onConfirmingChange, onImported }: MarketingImportFormProps) {
  const [template, setTemplate] = useState('');
  /** What each column is for; empty from a server that sends the header alone. */
  const [columns, setColumns] = useState<MarketingImportColumn[]>([]);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<MarketingImportPreview | null>(null);
  const [orders, setOrders] = useState<MarketingImportOrderResult[]>([]);
  /** Named only when the rows came from a file; pasted rows have no filename to record the batch under. */
  const [fileName, setFileName] = useState('');
  const [duplicateOf, setDuplicateOf] = useState<MarketingImportDuplicate | null>(null);
  const [batches, setBatches] = useState<MarketingImportBatch[]>([]);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The first read failing is a state the form stays in; every write reports through the toast [T03]. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toasts, showError, showFailure, dismiss } = useFulfillmentToast();
  const { user } = useAuth();
  const [usingCustomType, setUsingCustomType] = useState(false);
  const [customOrderType, setCustomOrderType] = useState('');
  /** Null until the desk edits it, so the box follows the logged-in user once the session has loaded. */
  const [picDraft, setPicDraft] = useState<string | null>(null);
  const previewRevisionRef = useRef(0);
  // The last unreadable-file card, closed by the next pick so a corrected file does not sit under an old error.
  const readFailureToastRef = useRef<number | null>(null);
  const batchesRequestRef = useRef(0);
  /** A two-letter code is not a country to most people, so the name rides with it. */
  const blockedNames = (preview?.blockedCountries ?? [])
    .map((country) => `${country.name} (${country.code})`)
    .join(', ');
  const blocked = (preview?.blockedCountries.length ?? 0) > 0;
  /** One type and one PIC cover the whole batch; every order the confirm creates is recorded under them. */
  const orderType = usingCustomType ? customOrderType.trim() : SEEDING_ORDER_TYPE;
  // An import is recorded under whoever raised it unless the desk names someone else.
  const picInput = picDraft ?? (user?.name || user?.email || '');
  const pic = picInput.trim();
  const batchComplete = orderType !== '' && pic !== '';
  /**
   * Nothing is imported while any row is refused or any destination has no carrier: the server refuses
   * the file whole, so the form has to stop before the desk spends a confirm finding that out.
   */
  const canConfirm =
    (preview?.rows.length ?? 0) > 0 && (preview?.rowErrors.length ?? 0) === 0 && !blocked && batchComplete;

  /**
   * Closing the modal unmounts the form while a file read or a preview may still be on its way: moving the
   * revisions on makes those late answers stale, and a closed form must not leave the modal held.
   */
  useEffect(
    () => () => {
      previewRevisionRef.current += 1;
      batchesRequestRef.current += 1;
      onConfirmingChange(false);
    },
    [onConfirmingChange],
  );

  useEffect(() => {
    let live = true;
    client.getMarketingImportTemplate().then(
      (response) => {
        if (live) {
          setTemplate(response.template);
          setColumns(response.columns ?? []);
          setLoadError(null);
        }
      },
      (error: unknown) => {
        if (live) {
          setLoadError(describeFailure(error, GENERIC_ERROR));
        }
      },
    );
    return () => {
      live = false;
    };
  }, [client]);

  /** Kept apart from the template read: the desk can still import while the record of past imports is down. */
  const loadBatches = useCallback(async () => {
    // A late first read must not replace the list a confirm already re-read.
    const request = ++batchesRequestRef.current;
    try {
      const response = await client.listMarketingImportBatches(RECENT_IMPORTS);
      if (request !== batchesRequestRef.current) {
        return;
      }
      setBatches(response.items);
      setBatchesError(null);
    } catch (error) {
      if (request !== batchesRequestRef.current) {
        return;
      }
      setBatchesError(describeFailure(error, 'Could not load recent imports.'));
    }
  }, [client]);

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  /**
   * Discards whatever the last preview said, because the rows it judged are no longer the rows on
   * screen, and returns the revision that now owns the form so a slower read can tell it was replaced.
   */
  const resetPreview = (): number => {
    setPreview(null);
    setOrders([]);
    setDuplicateOf(null);
    return ++previewRevisionRef.current;
  };

  const runPreview = async (sourceCsv: string) => {
    if (!sourceCsv.trim()) {
      return;
    }
    const revision = ++previewRevisionRef.current;
    setBusy(true);
    setPreview(null);
    setOrders([]);
    setDuplicateOf(null);
    try {
      // Extra columns and "1,000" quantities are the sheet's formatting, not the desk's mistake.
      const result = await client.previewMarketingImport(alignToTemplate(sourceCsv, template));
      if (revision === previewRevisionRef.current) {
        setPreview(result);
      }
    } catch (error) {
      showFailure(error, GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  /** A file and a paste meet here: the box shows what was read, so the rest of the form sees one input. */
  const onFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    if (readFailureToastRef.current !== null) {
      dismiss(readFailureToastRef.current);
      readFailureToastRef.current = null;
    }
    // Re-picking after a mis-click is normal, and a workbook can still be reading when the second
    // file arrives — whichever read finishes last, the desk's latest choice is the one that stands.
    const revision = resetPreview();
    let text: string;
    try {
      text = await readAsCsv(file);
    } catch {
      if (revision === previewRevisionRef.current) {
        // A failed read must not leave the box showing the file picked before as if it were this one (SSD-7778).
        if (fileName !== '') {
          setCsv('');
          setFileName('');
        }
        readFailureToastRef.current = showError(
          'Could not read that file. Save it as .csv, .xlsx, or .xls and try again.',
        );
      }
      return;
    }
    if (revision !== previewRevisionRef.current) {
      return;
    }
    setCsv(text);
    setFileName(file.name);
    await runPreview(text);
  };

  const confirm = async (overrideDuplicate = false) => {
    if (!preview || !canConfirm) {
      return;
    }
    const revision = previewRevisionRef.current;
    const rows = preview.rows;
    setBusy(true);
    onConfirmingChange(true);
    setDuplicateOf(null);
    try {
      const result = await client.confirmMarketingImport(rows, {
        orderType,
        picUser: pic,
        // The hash the preview took is the only thing that lets the server recognise a re-upload.
        fileHash: preview.fileHash,
        ...(fileName ? { filename: fileName } : {}),
        ...(overrideDuplicate ? { overrideDuplicate: true } : {}),
      });
      if (revision === previewRevisionRef.current) {
        setOrders(result.orders);
      }
      onImported();
      void loadBatches();
    } catch (error) {
      // A file already imported is a warning, not a refusal: nothing was created and the desk decides.
      const alreadyImported = duplicateFrom(error);
      if (alreadyImported) {
        setDuplicateOf(alreadyImported);
        return;
      }
      showFailure(error, GENERIC_ERROR);
    } finally {
      setBusy(false);
      onConfirmingChange(false);
    }
  };

  return (
    <div className="add-orders">
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
      <div className="add-orders-head">
        <p className="text-muted mb-0">
          Import manual gifting and marketing orders from a file. They join this list as marketing orders.
        </p>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={!template}
          onClick={() => downloadMarketingImportTemplate(template)}
        >
          <DownloadIcon />
          Download template (.xlsx)
        </Button>
      </div>
      {/* The file is the only form here, so what a carrier label needs is said before a preview refuses a row (SSD-7448). */}
      {columns.length > 0 && (
        <details className="add-orders-columns">
          <summary>
            Columns in the file · {columns.filter((column) => column.need === 'required').length} required,{' '}
            {columns.filter((column) => column.need === 'conditional').length} for some countries,{' '}
            {columns.filter((column) => column.need === 'optional').length} optional
          </summary>
          <Table size="sm" className="add-orders-table mb-0" aria-label="Marketing import columns">
            <colgroup>
              <col className="ao-col-name" />
              <col className="ao-col-need" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Column</th>
                <th>Needed</th>
                <th>What it is</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((column) => (
                <tr key={column.name}>
                  <td className="font-monospace">{column.name}</td>
                  <td className={column.need === 'optional' ? 'text-muted' : 'fw-semibold'}>
                    {NEED_LABEL[column.need]}
                  </td>
                  <td>{column.note}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </details>
      )}
      {loadError && (
        <Alert variant="danger" role="alert">
          {loadError}
        </Alert>
      )}
      {duplicateOf && (
        <Alert variant="warning">
          <Alert.Heading as="h6">This file was already imported</Alert.Heading>
          <p className="mb-2">
            Imported {kstTimestamp(duplicateOf.confirmedAt)} as batch {duplicateOf.batchId} (
            {plural(duplicateOf.orderCount, 'order')}). Nothing was created just now.
          </p>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setDuplicateOf(null)}>
              Cancel
            </Button>
            <Button size="sm" variant="warning" disabled={busy} onClick={() => void confirm(true)}>
              Import anyway
            </Button>
          </div>
        </Alert>
      )}
      <Row className="g-3 mb-3">
        <Col md="auto">
          <div className="form-label" id="marketing-order-type-label">
            Order type
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-start">
            <ButtonGroup size="sm" aria-labelledby="marketing-order-type-label">
              <Button
                variant="outline-secondary"
                active={!usingCustomType}
                aria-pressed={!usingCustomType}
                disabled={busy}
                onClick={() => setUsingCustomType(false)}
              >
                {SEEDING_ORDER_TYPE}
              </Button>
              <Button
                variant="outline-secondary"
                active={usingCustomType}
                aria-pressed={usingCustomType}
                aria-label="Custom order type"
                disabled={busy}
                onClick={() => setUsingCustomType(true)}
              >
                <PencilIcon />
                Custom
              </Button>
            </ButtonGroup>
            {usingCustomType && (
              <div className="add-orders-custom-type">
                <Form.Control
                  size="sm"
                  aria-label="Order type name"
                  placeholder="e.g. Pop-up event giveaway"
                  maxLength={BATCH_FIELD_MAX_LENGTH}
                  value={customOrderType}
                  disabled={busy}
                  isInvalid={orderType === ''}
                  onChange={(event) => setCustomOrderType(event.target.value)}
                />
                {orderType === '' && <Form.Control.Feedback type="invalid">Enter an order type.</Form.Control.Feedback>}
              </div>
            )}
          </div>
        </Col>
        <Col md={4}>
          <Form.Group controlId="marketing-pic">
            <Form.Label>PIC</Form.Label>
            <Form.Control
              size="sm"
              maxLength={BATCH_FIELD_MAX_LENGTH}
              value={picInput}
              disabled={busy}
              isInvalid={pic === ''}
              onChange={(event) => setPicDraft(event.target.value)}
            />
            {pic === '' && <Form.Control.Feedback type="invalid">Enter a PIC name.</Form.Control.Feedback>}
            <Form.Text className="text-muted">
              Defaults to you. Recorded as the PIC of every order in this import.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Form.Group controlId="marketing-file">
        <Form.Label>Upload marketing file</Form.Label>
        <Form.Control
          type="file"
          accept={MARKETING_IMPORT_ACCEPT}
          disabled={busy}
          onChange={(event) => {
            const input = event.target as HTMLInputElement;
            void onFile(input.files?.[0]);
            // Re-uploading the corrected file under the same name has to fire change again.
            input.value = '';
          }}
        />
        <Form.Text className="text-muted">
          A .csv, .xlsx or .xls is read into the box below and previewed at once. Paste the rows in directly when there
          is no file.
        </Form.Text>
      </Form.Group>
      <Form.Group controlId="marketing-csv" className="mt-3">
        <Form.Label>Marketing CSV</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          className="font-monospace"
          value={csv}
          disabled={busy}
          onChange={(event) => {
            resetPreview();
            setCsv(event.target.value);
            // Edited by hand, the rows are no longer the file that was uploaded under that name.
            setFileName('');
          }}
        />
      </Form.Group>
      <div className="d-flex gap-2 mt-3">
        <Button disabled={busy || !csv.trim()} onClick={() => void runPreview(csv)}>
          Preview
        </Button>
        {preview && (
          <Button variant="success" disabled={busy || !canConfirm} onClick={() => void confirm()}>
            Confirm import ({plural(preview.rows.length, 'row')})
          </Button>
        )}
      </div>
      {preview && (
        <div className="mt-3">
          <div className="fw-semibold">
            Preview{fileName ? ` — ${fileName}` : ''} · {plural(preview.rows.length, 'row')} parsed ·{' '}
            {plural(preview.rowErrors.length, 'error')}
          </div>
          <Table size="sm" hover className="add-orders-table mt-2 align-middle" aria-label="Marketing import preview">
            <colgroup>
              <col className="ao-recipient" />
              <col className="ao-country" />
              <col className="ao-sku" />
              <col />
              <col className="ao-qty" />
              <col className="ao-campaign" />
              <col className="ao-carrier" />
            </colgroup>
            <thead>
              <tr>
                <th>Recipient</th>
                <th>Country · State</th>
                <th>SKU</th>
                <th>Product Name</th>
                <th>Qty</th>
                <th>Campaign</th>
                <th>Carrier (auto)</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.slice(0, PREVIEW_ROWS).map((row, index) => (
                <tr key={`${row.campaignId}-${row.influencerId}-${row.sku}-${index}`}>
                  <td>{row.recipientName}</td>
                  {/* The state as the server will print it: a US or CA name comes back as its two-letter code. */}
                  <td>
                    {row.countryCode}
                    {row.state ? ` · ${row.state}` : ''}
                  </td>
                  <td className="font-monospace">{row.sku}</td>
                  {/* No name means the catalog does not know this sku — a typo, caught before confirming. */}
                  <td className={row.productName ? undefined : 'text-danger fw-semibold'}>
                    {row.productName ?? 'Unknown SKU'}
                  </td>
                  <td className="font-monospace">{row.qty}</td>
                  <td>{row.campaignId}</td>
                  {/* A blank carrier is the reason the confirm below is refused, so it is never left implicit. */}
                  <td className={row.carrier ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                    {row.carrier ?? 'Not connected'}
                  </td>
                </tr>
              ))}
              {preview.rows.length > PREVIEW_ROWS && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    ⋯ +{preview.rows.length - PREVIEW_ROWS} more rows
                  </td>
                </tr>
              )}
              {preview.rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    {EMPTY_CELL}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {/* The router also weighs declared value and product categories; a file carries no prices. */}
          <div className="text-muted small mb-2">
            Carrier is decided by destination. The shipment itself may still fall to another carrier once its declared
            value is known.
          </div>
          {preview.mergeNotice && (
            <Alert variant="warning" className="py-2 mb-2">
              {mergeNoticeText(preview.mergeNotice)}
            </Alert>
          )}
          {/*
            The server numbers a reason by the file's own row, header included, as the sheet shows it, and
            leaves a refused row out of the table above — so the reason cannot sit beside a table row.
          */}
          {preview.rowErrors.map((item) => (
            <Alert variant="danger" className="py-2 mb-2" key={`${item.row}-${item.message}`}>
              Row {item.row}: {item.message}
            </Alert>
          ))}
          {blocked && (
            <Alert variant="danger" className="mb-0">
              Cannot import — these countries have no connected carrier: {blockedNames}. Ask the fulfillment team to
              connect them, or remove those rows and preview again.
            </Alert>
          )}
        </div>
      )}
      {orders.length > 0 && (
        <Alert variant={orders.every((order) => order.created) ? 'success' : 'warning'} className="mt-3 mb-0">
          {/* Linked, because a marketing order sorts below the web orders and is not on the list's first page. */}
          Result:{' '}
          {orders.map((order, index) => (
            <Fragment key={order.orderId}>
              {index > 0 && ', '}
              <Link to={ROUTES.fulfillment.orderDetail(order.orderId)}>#{order.orderId}</Link> (
              {order.created ? 'created' : 'existing'})
            </Fragment>
          ))}
        </Alert>
      )}
      {/* Who imported what and when outlives the toast: the server records every confirm as a batch. */}
      <h3 className="add-orders-sub">Recent imports</h3>
      {batchesError ? (
        <div className="text-danger small">{batchesError}</div>
      ) : (
        <Table size="sm" hover className="add-orders-table mb-0 align-middle" aria-label="Recent marketing imports">
          <colgroup>
            <col className="ao-at" />
            <col className="ao-by" />
            <col />
            <col className="ao-type" />
            <col className="ao-pic" />
            <col className="ao-orders" />
          </colgroup>
          <thead>
            <tr>
              <th>Imported at</th>
              <th>Imported by</th>
              <th>File</th>
              <th>Order type</th>
              <th>PIC</th>
              <th>Orders</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((batch) => (
              <tr key={batch.batchId}>
                <td className="font-monospace small">{kstTimestamp(batch.confirmedAt)}</td>
                <td>{batch.operator ?? EMPTY_CELL}</td>
                <td>{batch.filename ?? <span className="text-muted">Pasted rows</span>}</td>
                <td>{batch.orderType ?? EMPTY_CELL}</td>
                <td>{batch.picUser ?? EMPTY_CELL}</td>
                <td className="font-monospace">
                  {batch.createdCount === batch.orderCount
                    ? batch.orderCount
                    : `${batch.createdCount} created / ${batch.orderCount}`}
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No imports yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
}

interface AddOrdersModalProps {
  readonly show: boolean;
  readonly client: FulfillmentAdminClient;
  readonly onHide: () => void;
  /** Called once a confirm created or matched orders, so the list behind the modal can show them. */
  readonly onImported: () => void;
}

/**
 * Marketing and gifting orders are added from the Orders list they land on (SSD-7448): this used to be its
 * own Marketing & Samples screen, which the desk left the list for and came back from to find the orders.
 * The modal only mounts its form while open, so every opening starts from a clean file and a fresh read.
 */
export function AddOrdersModal({ show, client, onHide, onImported }: AddOrdersModalProps) {
  const [confirming, setConfirming] = useState(false);

  // An import in flight cannot be called back, so the modal stays until the server has answered. A preview
  // or a file read can be left: the form drops their answers once it is closed.
  const hide = () => {
    if (!confirming) {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={hide}
      size="xl"
      backdrop="static"
      keyboard={!confirming}
      aria-labelledby="add-orders-title"
      {...ULTIMATE_MODAL}
    >
      <Modal.Header closeButton={!confirming}>
        <Modal.Title id="add-orders-title">Add marketing orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MarketingImportForm client={client} onConfirmingChange={setConfirming} onImported={onImported} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={confirming} onClick={hide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
